import async from 'async';
import _ from 'lodash';
import hash from 'object-hash';
import socketIOClient from 'socket.io-client';

import { intl } from '../Intl/IntlGlobalWrapper';
import dataService from '../../services/data.service';
import { setError } from './ErrorsActions';

// Export Constants
export const UPDATE_PAGE_NAME = 'APP_UPDATE_PAGE_NAME';
export const LOADING_DATA = 'APP_LOADING_DATA';
export const DASHBOARD_DATA = 'APP_DASHBOARD_DATA';
export const DASHBOARD_ANALYSIS_DATA = 'APP_DASHBOARD_ANALYSIS_DATA';
export const FIELD_DATA = 'APP_FIELD_DATA';
export const SET_OUTSIDE = 'APP_SET_OUTSIDE';
export const SET_APP = 'APP_SET_APP';
export const SET_WALLMODE = 'APP_SET_WALLMODE';
export const NAV_SHOWN = 'APP_NAVIGATION_SHOWN';
export const NAV_EXPANDED = 'APP_NAVIGATION_EXPANDED';
export const NAV_OPENED = 'APP_NAVIGATION_OPENED';
export const OVERVIEW_LOADING = 'APP_OVERVIEW_LOADING';
export const ANALYSIS_LOADING = 'APP_ANALYSIS_LOADING';
export const LAST_UPDATED = 'APP_LAST_UPDATED';
export const PROGRESS = 'APP_PROGRESS';

const MAX_RETRIES = 3600;
let prevHash;
let prevAnalysisHash;
let socket = undefined;
let lastId = false;

function setLastUpdated(date) {
  return {
    type: LAST_UPDATED,
    date
  };
}

// Export Actions
export function setPageName(name) {
  return {
    type: UPDATE_PAGE_NAME,
    pageName: name
  };
}

export function setOutside(context) {
  return {
    type: SET_OUTSIDE,
    plugin: context
  };
}

export function setApp(app) {
  const appName = intl.formatMessage({ id: `${app}.appname` });
  return {
    type: SET_APP,
    app,
    appName
  };
}

export function setWallmode() {
  return {
    type: SET_WALLMODE,
    value: true
  };
}

export function isLoading(bool) {
  return {
    type: LOADING_DATA,
    isLoading: bool
  };
}

export function overviewIsLoading(bool) {
  return {
    type: OVERVIEW_LOADING,
    overviewLoading: bool
  };
}

export function analysisIsLoading(bool) {
  return {
    type: ANALYSIS_LOADING,
    analysisLoading: bool
  };
}

export function toggleNavigationShown(show) {
  if (show) {
    document.body.classList.add('menu-shown');
  } else {
    document.body.classList.remove('menu-shown');
  }

  return {
    type: NAV_SHOWN,
    navigationShown: show
  };
}

export function toggleNavigationExpanded(expanded) {
  return {
    type: NAV_EXPANDED,
    navigationExpanded: expanded
  }
}

export function toggleNavigationMenuOpened(opened) {
  return {
    type: NAV_OPENED,
    navigationMenuOpened: opened
  }
}

export function updateFieldData(field, data) {
  return dispatch => {
    dispatch({
      type: FIELD_DATA,
      field,
      data
    });
  };
}

export function loadData(dashboardId, dates, disableCfActivities, onSynced) {
  let retries = 0,
    timeout,
    lastUpdatedVal,
    lastVal;

  return dispatch => {
    lastId = dashboardId;
    prevHash = undefined;
    prevAnalysisHash = undefined;
    dispatch(isLoading(true));
    !!timeout && clearInterval(timeout);

    timeout = setInterval(poll, 1500, dispatch, dashboardId, dates, disableCfActivities);
  };

  function poll(dispatch, dashboardId, dates, disableCfActivities) {
    if (lastId !== dashboardId) {
      !!timeout && clearInterval(timeout);
      return;
    }

    if (retries >= MAX_RETRIES) {
      !!timeout && clearInterval(timeout);
      dispatch(setError('Could not load data'));
      return;
    }

    dataService
      .checkPullingState(dashboardId)
      .then(async res => {
        if (lastId !== dashboardId) {
          !!timeout && clearInterval(timeout);
          return;
        }

        if (res.running && !lastVal) {
          lastVal = res.progress;
          !!lastVal && dispatch({
            type: PROGRESS,
            progress: lastVal
          });
        }

        lastUpdatedVal = res.lastRunAt || lastUpdatedVal;

        if (res.noJob || res.completed || res.failed) {
          retries = 0;
          !!timeout && clearInterval(timeout);
          dispatch(setLastUpdated(lastUpdatedVal));
          const fetchDashboard = () => fetchData(dispatch, dashboardId, dates, disableCfActivities);
          if (onSynced) onSynced(fetchDashboard);
          else fetchDashboard();
          return;
        }
        retries++;
      })
      .catch(err => {
        !!timeout && clearInterval(timeout);
        dispatch(setError(err));
      });
  }

  function fetchData(dispatch, dashboardId, dates, disableCfActivities) {
    if (lastId !== dashboardId) return;

    const md5 = hash.MD5({
      dashboardId,
      dates: {
        s: dates.startDate.toISOString(),
        e: dates.endDate.toISOString()
      }
    });
    if (prevHash === md5) {
      return false;
    }
    prevHash = md5;

    dataService
      .getBlockedActivities(dashboardId, dates)
      .then(blocked => {
        if (lastId !== dashboardId) return;

        async.parallel(
          [
            callback => {
              success('blocked', blocked, callback);
            },
            callback => {
              dataService
                .updateLastAccessed(dashboardId)
                .then(() => callback())
                .catch(err => fail(err, callback));
            },
            callback => {
              dataService
                .getActivities(dashboardId, dates)
                .then(data => success('activities', data, callback))
                .catch(err => fail(err, callback));
            },
            callback => {
              if (!disableCfActivities) {
                dataService
                  .getCfActivities(dashboardId, dates)
                  .then(data => success('cfActivities', data, callback))
                  .catch(err => fail(err, callback));
              } else {
                success('cfActivities', [], callback);
              }
            },
            callback => {
              dataService
                .getCards(dashboardId, dates)
                .then(data => success('cards', data, callback))
                .catch(err => fail(err, callback));
            },
            callback => {
              dataService
                .getLists(dashboardId)
                .then(data => success('lists', data, callback))
                .catch(err => fail(err, callback));
            },
            callback => {
              dataService
                .getLabels(dashboardId, dates)
                .then(data => success('labels', data, callback))
                .catch(err => fail(err, callback));
            },
            callback => {
              dataService
                .getMembers(dashboardId, dates)
                .then(data => success('members', data, callback))
                .catch(err => fail(err, callback));
            },
            callback => {
              dataService
                .getCustomFields(dashboardId, dates)
                .then(data => success('customFields', data, callback))
                .catch(err => fail(err, callback));
            }
          ],
          (err, results) => {
            if (err) {
              return dispatch(setError(err));
            }

            if (lastId !== dashboardId) return;

            let data = _.mapValues(_.keyBy(results, 'name'), 'data');
            dispatch({
              type: DASHBOARD_DATA,
              data: data
            });
          }
        );
      })
      .catch(err => {
        return dispatch(setError(err));
      });
  }  
}

function success(objname, data, cb) {
  cb(null, { name: objname, data });
}

function fail(err, cb) {
  cb(err);
}

export function fetchAnalysisData(dates, dashboardId, disableCfActivities) {
  return async (dispatch) => {
    dispatch(analysisIsLoading(true));
    const md5 = hash.MD5({
      dashboardId,
      dates: {
        s: dates.startDate.toISOString(),
        e: dates.endDate.toISOString()
      }
    });

    if (prevAnalysisHash === md5) {
      dispatch(analysisIsLoading(false));
      return;
    }
    prevAnalysisHash = md5;

    try {
      // Fetch all data in parallel
      const [
        blocked,
        activities,
        cfActivities,
        cards,
        lists,
        labels,
        members,
        customFields
      ] = await Promise.all([
        dataService.getBlockedActivities(dashboardId, dates),
        dataService.getActivities(dashboardId, dates),
        disableCfActivities 
          ? Promise.resolve([])
          : dataService.getCfActivities(dashboardId, dates),
        dataService.getCards(dashboardId, dates),
        dataService.getLists(dashboardId),
        dataService.getLabels(dashboardId, dates),
        dataService.getMembers(dashboardId, dates),
        dataService.getCustomFields(dashboardId, dates)
      ]);

      // Transform data into the expected format
      const data = {
        blocked,
        activities,
        cfActivities: disableCfActivities ? [] : cfActivities,
        cards,
        lists,
        labels,
        members,
        customFields
      };

      // Dispatch the success action
      dispatch({
        type: DASHBOARD_ANALYSIS_DATA,
        data
      });

      return data; // Return data for awaiting

    } catch (err) {
      dispatch(setError(err));
      throw err; // Propagate error for proper error handling
    }
  };
}

function setSocketProgressEventHandler(updates) {
  return _(updates)
    .filter(u => !!u.data)
    .map(u => {
      return u.data;
    })
    .first();
}

export function setSocketDataEventHandler(data, type, updates) {
  const typeData = data[type];

  _(updates)
    .filter(u => !!u.data)
    .forEach(u => {
      try {
        let aIndex;
        if (type == 'blocked' || type == 'cfActivities') {
          aIndex = _.findIndex(typeData, { cardId: u.data.cardId, startDate: u.data.startDate });
        } else {
          aIndex = _.findIndex(typeData, { extId: u.data.extId });
        }
        switch (u.type) {
          case 'update': {
            if (aIndex > -1) {
              typeData[aIndex] = u.data;
            } else {
              typeData.push(u.data);
            }
            break;
          }
          case 'updateoption': {
            if (aIndex > -1) {
              const options = _(u.data.options)
                .map(o => {
                  const opt = _.find(typeData[aIndex].options, { _id: o._id });
                  if (opt) {
                    if (data.app === 'jira') {
                      const name = u.data.name && u.data.name.toLowerCase();
                      if (name === 'epic link' || name === 'parent') {
                        o.value = opt.value;
                      }
                    }
                    o.history = opt.history;
                    return o;
                  }
                })
                .filter(o => !!o)
                .value();

              typeData[aIndex].options = _.intersectionBy(options, typeData[aIndex].options, '_id');
            }
            break;
          }
          case 'updatelabelscolor': {
            _.forEach(typeData, t => {
              const opt = _.find(u.data, o => {
                return t._id === o._id;
              });
              if (opt) {
                t.color = opt.color;
              }
            });
            break;
          }
          case 'delete': {
            if (aIndex > -1) {
              typeData.splice(aIndex, 1);
            }
            break;
          }
        }
      } catch (err) {
        return Promise.reject(err);
      }
    });

  return Promise.resolve(data);
}

export function initSocket(boardId) {
  return (dispatch, getState) => {
    if (!!socket) {
      socket.close();
    }

    socket = socketIOClient(process.env.SOCKETS_HOST, {
      path: process.env.RUN_ENV === 'production' || process.env.RUN_ENV === 'development' ? '/ws' : '/pullserver/ws',
      query: {
        boardId
      }
    });

    socket.on('job_progress', updates => {
      const progress = setSocketProgressEventHandler(updates);
      dispatch({
        type: PROGRESS,
        progress
      });
    });

    _.forEach(
      ['activities', 'cards', 'labels', 'lists', 'members', 'customFields', 'blocked', 'cfActivities'],
      type => {
        socket.on(type, updates => {
          if (getState().app.isLoading) return;

          setSocketDataEventHandler(getState().app.data, type, updates).then(
            res => {
              dispatch({
                type: DASHBOARD_DATA,
                fieldType: type,
                data: res
              });
            },
            err => {
              dispatch(setError(`Socket event error: ${err.message}`, { show: false }));
            }
          );
        });
      }
    );

    socket.on('connect_error', err => {
      console.log('Socket connection error', err);
    });

    socket.on('error', err => {
      console.log('Socket error', err);
    });

    socket.on('disconnect', reason => {
      if (reason === 'io server disconnect') {
        // the disconnection was initiated by the server, you need to reconnect manually
        socket.connect();
      }
    });
  };
}

export function closeSocket() {
  if (!!socket) {
    socket.close();
  }
}
